// Per-fragment hex maths in TSL, mirroring src/core/grid.ts.
//
// A fragment's world XZ is turned into its odd-r hex (col, row) by cube rounding, then into a
// local offset from the hex centre and the distance to each of the six edges:
//
//            4 NW   5 NE          edge d has outward normal n_d at 60·d degrees (+x → +z),
//              ___/\___           so dist_d = inradius − dot(local, n_d):
//     3 W     |   ·    |    0 E     0 at the edge, √3/2 at the centre.
//              ‾‾‾\/‾‾‾
//            2 SW   1 SE
//
// Everything here works on wrapped x, so all ±WORLD_W copies of a chunk shade identically.

import { abs, clamp, floor, float, int, ivec2, min, mod, round, select, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { COLS, HEX_W, ROWS, ROW_H, WORLD_W } from '../../core/grid.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;

const SQRT3 = Math.sqrt(3);

/** Distance from a hex centre to the middle of an edge. */
export const INRADIUS = SQRT3 / 2;

// Outward edge normals, edge d at 60·d degrees (matches grid.ts DIR and cornerOffset).
const EDGE_NORMAL: ReadonlyArray<readonly [number, number]> = Array.from({ length: 6 }, (_, d) => {
  const a = (Math.PI / 3) * d;
  return [Math.cos(a), Math.sin(a)] as const;
});

// [dcol, drow] per direction for even and odd rows (grid.ts NEIGHBOUR_EVEN / NEIGHBOUR_ODD).
const NEIGHBOUR_EVEN: ReadonlyArray<readonly [number, number]> = [[1, 0], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1]];
const NEIGHBOUR_ODD: ReadonlyArray<readonly [number, number]> = [[1, 0], [1, 1], [0, 1], [-1, 0], [0, -1], [1, -1]];

/** A hex cell as float nodes holding integer values. */
export interface HexCell {
  col: F;
  row: F;
}

/** Everything the surface layers know about the fragment's hex. */
export interface HexFrag extends HexCell {
  /** Axial coordinates (pointy-top) of the hex, before the row clamp and column wrap. */
  q: F;
  r: F;
  /** Texel coordinate of the hex in the per-hex data textures. */
  coord: Node<'ivec2'>;
  /** Fragment offset from the hex centre, (east, south) in world units. */
  local: V2;
  /** Hex centre in wrapped world XZ. */
  centre: V2;
  /** Distance from the fragment to edge d, d = 0..5 (grid.ts DIR). */
  edge: F[];
  /** Smallest of `edge`: 0 on the hex outline, INRADIUS at the centre. */
  minEdge: F;
}

/** Wrap a world x into [0, WORLD_W). */
export function wrapXNode(x: F): F {
  return mod(x, WORLD_W);
}

/**
 * The hex containing world XZ (cube rounding, as grid.ts hexAtWorld). `name` prefixes the shader
 * variables, so one shader can hold several lookups.
 */
export function hexFrag(xz: V2, name = 'hex'): HexFrag {
  const px = wrapXNode(xz.x).sub(HEX_W / 2);
  const pz = xz.y.sub(ROW_H / 2);

  // Pixel → fractional axial (pointy-top, size 1), then cube round.
  const q = px.mul(SQRT3 / 3).sub(pz.div(3));
  const r = pz.mul(2 / 3);
  const s = q.add(r).negate();
  const rq = round(q);
  const rr = round(r);
  const rs = round(s);
  const dq = abs(rq.sub(q));
  const dr = abs(rr.sub(r));
  const ds = abs(rs.sub(s));
  const fixQ = dq.greaterThan(dr).and(dq.greaterThan(ds));
  const fixR = ds.lessThanEqual(dr);
  const aq = select(fixQ, rr.add(rs).negate(), rq).toVar(`${name}Q`);
  const ar = select(fixQ, rr, select(fixR, rq.add(rs).negate(), rr)).toVar(`${name}R`);

  // Axial → odd-r offset; rows clamp at the poles, columns wrap.
  const row = clamp(ar, 0, ROWS - 1).toVar(`${name}Row`);
  const col = mod(aq.add(floor(ar.mul(0.5))), COLS).toVar(`${name}Col`);

  // Local offset from the axial centre (no wrap involved).
  const local = vec2(px.sub(aq.add(ar.mul(0.5)).mul(SQRT3)), pz.sub(ar.mul(1.5))).toVar(`${name}Local`);
  const parity = mod(row, 2);
  const centre = vec2(col.add(0.5).add(parity.mul(0.5)).mul(HEX_W), row.add(0.5).mul(ROW_H));

  const edge: F[] = EDGE_NORMAL.map(([nx, nz]) => float(INRADIUS).sub(local.x.mul(nx).add(local.y.mul(nz))));
  let minEdge = edge[0];
  for (let d = 1; d < 6; d++) {
    minEdge = min(minEdge, edge[d]);
  }

  return { col, row, q: aq, r: ar, coord: cellCoord({ col, row }), local, centre, edge, minEdge: minEdge.toVar(`${name}MinEdge`) };
}

// Axial (dq, dr) of the neighbour in direction d; its centre lies √3 away at 60·d degrees.
const AXIAL_DIR: ReadonlyArray<readonly [number, number]> = [[1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1]];
const SECTOR = Math.PI / 3;

/** Cell of the axial hex (q, r): rows clamp at the poles, columns wrap. */
function axialCell(q: F, r: F): HexCell {
  return { row: clamp(r, 0, ROWS - 1), col: mod(q.add(floor(r.mul(0.5))), COLS) };
}

/** Axial neighbour offset for a direction held in a float node (0..5). */
function axialDir(d: F, axis: 0 | 1): F {
  let out: F = float(AXIAL_DIR[5][axis]);
  for (let k = 4; k >= 0; k--) {
    out = select(d.lessThan(k + 0.5), float(AXIAL_DIR[k][axis]), out);
  }
  return out;
}

/** Three hex centres around a fragment and its barycentric weights among them. */
export interface HexBary {
  cells: readonly [HexCell, HexCell, HexCell];
  /** Weights of cells[0] (own hex), cells[1], cells[2]; they sum to 1. */
  w: Node<'vec3'>;
}

/**
 * The triangle of hex centres (own, neighbour d, neighbour d+1) that contains the fragment, with
 * barycentric weights: exact linear interpolation of per-hex data between centres, no seams at the
 * odd-row offset.
 *
 *          n(d+1)
 *           ╱ ╲        the fragment's angle around its own centre picks sector d;
 *          ╱ · ╲       rotating by −60·d puts the sector's edges at 0° and 60°.
 *      own ───── n(d)
 */
export function hexBary(frag: HexFrag): HexBary {
  const angle = frag.local.y.atan(frag.local.x);
  const sector = floor(mod(angle, 2 * Math.PI).div(SECTOR)).min(5).toVar();
  const next = mod(sector.add(1), 6);

  // Rotate the local offset by −60·d, then solve p = a·e0 + b·e60 (|e| = √3).
  const phi = sector.mul(-SECTOR);
  const c = phi.cos();
  const sn = phi.sin();
  const px = frag.local.x.mul(c).sub(frag.local.y.mul(sn));
  const pz = frag.local.x.mul(sn).add(frag.local.y.mul(c));
  const b = pz.div(1.5).clamp(0, 1);
  const a = px.div(SQRT3).sub(b.mul(0.5)).clamp(0, 1);
  const lin = vec3(float(1).sub(a).sub(b).max(0), a, b);

  // Ease each weight (smoothstep) and renormalise: gradients stop showing the triangle edges.
  const eased = lin.mul(lin).mul(lin.mul(-2).add(3));
  const w = eased.div(eased.x.add(eased.y).add(eased.z).max(1e-5));

  const cellD = axialCell(frag.q.add(axialDir(sector, 0)), frag.r.add(axialDir(sector, 1)));
  const cellN = axialCell(frag.q.add(axialDir(next, 0)), frag.r.add(axialDir(next, 1)));
  return { cells: [{ col: frag.col, row: frag.row }, cellD, cellN], w };
}

/** Texel coordinate for a cell. */
export function cellCoord(cell: HexCell): Node<'ivec2'> {
  return ivec2(int(cell.col), int(cell.row));
}

/** Neighbour of a cell in direction `dir` (0..5). Columns wrap; rows clamp at the poles. */
export function neighbourCell(cell: HexCell, dir: number): HexCell {
  const [ec, er] = NEIGHBOUR_EVEN[dir];
  const [oc] = NEIGHBOUR_ODD[dir];
  const odd = mod(cell.row, 2).greaterThan(0.5);
  const dc = select(odd, float(oc), float(ec));
  return {
    col: mod(cell.col.add(dc), COLS),
    row: clamp(cell.row.add(er), 0, ROWS - 1),
  };
}

/** Distance from `local` to the segment of edge d (a capsule, so corners round off). */
export function edgeSegmentDistance(local: V2, dir: number): F {
  const a0 = ((60 * dir - 30) * Math.PI) / 180;
  const a1 = ((60 * dir + 30) * Math.PI) / 180;
  const ax = Math.cos(a0);
  const az = Math.sin(a0);
  const bx = Math.cos(a1);
  const bz = Math.sin(a1);

  // Project onto the edge (length 1) and clamp to its ends.
  const ex = bx - ax;
  const ez = bz - az;
  const t = clamp(local.x.sub(ax).mul(ex).add(local.y.sub(az).mul(ez)), 0, 1);
  const dx = local.x.sub(t.mul(ex).add(ax));
  const dz = local.y.sub(t.mul(ez).add(az));
  return vec2(dx, dz).length();
}
